// [PURPOSE]: AppearanceController — syncs accessibility settings to root HTML attributes.
// [WHY]: A CSS-overlay architecture (themes.css) requires data-a11y tokens on
//   <html> so descendant selectors cascade without touching component JSX.
//   This null-rendering component is the single place that reads settings and
//   writes to document.documentElement — keeping the side-effect isolated.
//
// [PLACEMENT]: Mounted INSIDE <SettingsProvider> (via AppProviders) so
//   useSettings() is available. Returns null — no DOM output.
//
// [ADDITIVE / REVERSIBLE]: When all accessibility flags are false (default),
//   the attribute is removed entirely, making themes.css 100% inert.
//
// [data-theme plumbing]: Also sets data-theme from settings.theme when present,
//   laying the groundwork for future light / alternate theme support without
//   requiring a component change later.

import { useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { deriveA11yTokens } from '../utils/a11yTokens';

export default function AppearanceController() {
  const { settings } = useSettings();

  // ── data-a11y ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const tokenStr = deriveA11yTokens(settings.accessibility);
    const root = document.documentElement;

    if (tokenStr) {
      root.setAttribute('data-a11y', tokenStr);
    } else {
      // All off → remove attribute so themes.css selectors never match
      root.removeAttribute('data-a11y');
    }
  }, [settings.accessibility]);

  // ── data-theme plumbing (future light/alternate themes) ─────────────────
  useEffect(() => {
    const root = document.documentElement;
    if (settings.theme) {
      root.setAttribute('data-theme', settings.theme);
    } else {
      root.removeAttribute('data-theme');
    }
  }, [settings.theme]);

  return null;
}
